// ═══════════════════════════════════════════════════════════════
// Settings — Roles Tab
// Role management with permission matrix
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  NumberInput, Switch, Modal, ActionIcon, Stack, Table, ScrollArea,
} from '../../components/gab/GabComponents';
import { IconPlus, IconPencil, IconTrash } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useRoleStore } from '../../store/roleStore';
import type { Role } from '../../types/rbac';
import { emptyPermissions } from '../../types/rbac';
import type { RbacPermissions } from '../../types/rbac';
import PermissionMatrix from './PermissionMatrix';

interface RoleFormState {
  roleName: string;
  roleSlug: string;
  description: string;
  isDefault: boolean;
  sortOrder: number;
  permissions: RbacPermissions;
}

const EMPTY_FORM: RoleFormState = {
  roleName: '',
  roleSlug: '',
  description: '',
  isDefault: false,
  sortOrder: 99,
  permissions: emptyPermissions(),
};

function nameToSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

export default function RolesTab() {
  const { roles, loading, loadRoles, createRole, updateRole, deleteRole } = useRoleStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<RoleFormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadRoles(); }, []);

  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => a.sortOrder - b.sortOrder),
    [roles],
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (role: Role) => {
    setEditingId(role.id);
    setForm({
      roleName: role.roleName,
      roleSlug: role.roleSlug,
      description: role.description,
      isDefault: role.isDefault,
      sortOrder: role.sortOrder,
      permissions: { ...role.permissions },
    });
    setModalOpen(true);
  };

  const handleNameChange = (name: string) => {
    setForm(prev => ({
      ...prev,
      roleName: name,
      // Only auto-slug when creating
      ...(editingId === null ? { roleSlug: nameToSlug(name) } : {}),
    }));
  };

  const handleSave = async () => {
    if (!form.roleName.trim() || !form.roleSlug.trim()) {
      notifications.show({ message: 'Role name and slug are required', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      if (editingId !== null) {
        await updateRole(editingId, {
          roleName: form.roleName,
          roleSlug: form.roleSlug,
          description: form.description,
          isDefault: form.isDefault,
          sortOrder: form.sortOrder,
          permissions: form.permissions,
        });
        notifications.show({ message: 'Role updated', color: 'green' });
      } else {
        await createRole({
          roleName: form.roleName,
          roleSlug: form.roleSlug,
          description: form.description,
          isDefault: form.isDefault,
          sortOrder: form.sortOrder,
          permissions: form.permissions,
        });
        notifications.show({ message: 'Role created', color: 'green' });
      }
      setModalOpen(false);
    } catch {
      notifications.show({ message: 'Failed to save role', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRole(deleteTarget.id);
      notifications.show({ message: `Role "${deleteTarget.roleName}" deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch {
      notifications.show({ message: 'Failed to delete role', color: 'red' });
    }
  };

  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Roles</Title>
        <Button leftSection={<IconPlus size={14} />} size="xs" color="#64748B" onClick={openCreate}>
          Add Role
        </Button>
      </Group>

      <Paper withBorder>
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Slug</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Default</Table.Th>
                <Table.Th>Sort</Table.Th>
                <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedRoles.length === 0 && !loading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="lg">No roles defined yet</Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {sortedRoles.map(role => (
                <Table.Tr key={role.id}>
                  <Table.Td fw={500}>{role.roleName}</Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{role.roleSlug}</Text></Table.Td>
                  <Table.Td><Text size="sm" lineClamp={1}>{role.description}</Text></Table.Td>
                  <Table.Td>
                    {role.isDefault && <Badge color="blue" size="sm">Default</Badge>}
                  </Table.Td>
                  <Table.Td>{role.sortOrder}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="subtle" color="blue" size="sm" onClick={() => openEdit(role)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" size="sm" onClick={() => setDeleteTarget(role)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* ── Create / Edit Modal ───────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId !== null ? 'Edit Role' : 'Add Role'}
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Role Name"
            required
            value={form.roleName}
            onChange={e => handleNameChange(e.target.value)}
          />
          <TextInput
            label="Role Slug"
            required
            value={form.roleSlug}
            onChange={e => setForm(prev => ({ ...prev, roleSlug: e.target.value }))}
          />
          <Textarea
            label="Description"
            value={form.description}
            onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
          />
          <Group>
            <Switch
              label="Default Role"
              checked={form.isDefault}
              onChange={e => setForm(prev => ({ ...prev, isDefault: e.currentTarget.checked }))}
            />
            <NumberInput
              label="Sort Order"
              value={form.sortOrder}
              onChange={val => setForm(prev => ({ ...prev, sortOrder: Number(val) || 0 }))}
              min={0}
              max={999}
              style={{ width: 120 }}
            />
          </Group>

          <Text fw={600} size="sm" mt="sm">Permission Matrix</Text>
          <PermissionMatrix
            permissions={form.permissions}
            onChange={permissions => setForm(prev => ({ ...prev, permissions }))}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="#64748B" loading={saving} onClick={handleSave}>
              {editingId !== null ? 'Save Changes' : 'Create Role'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete Confirmation ───────────────────────────────── */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Role"
        size="sm"
      >
        <Text mb="md">
          Are you sure you want to delete <strong>{deleteTarget?.roleName}</strong>? This cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </>
  );
}
